import { renderAsync } from "docx-preview";

import {
  A4_WIDTH_72DPI,
  DOCX_PREVIEW_ID,
  DOCX_PREVIEW_STYLES_ID,
  DOX_ID_PREFIX,
  PAGE_ID_PREFIX,
} from "../constants";
import downloadFileFromUrl from "./downloadFileFromUrl";
import normalizeAnchorText from "./normalizeAnchorText";

const toNumber = (value = "", fallback = A4_WIDTH_72DPI) => {
  return Number(value.replace(/[^\d.-]/g, "") || fallback);
};

// A single-section document is virtualized by the article's child nodes rather than by page
// (see useDocxDocument), so a paragraph's row is the position of its top-level ancestor in that
// same list — text nodes included, since they occupy a row too. Paragraph ids count <p> elements
// across the whole document, which is a different sequence entirely.
const buildParagraphRowMap = (pages) => {
  const rowByParagraph = {};

  if (pages.length !== 1) {
    return rowByParagraph;
  }

  const [article] = pages[0].getElementsByTagName("article");

  if (!article) {
    return rowByParagraph;
  }

  Array.from(article.childNodes).forEach((child, rowIndex) => {
    if (child.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const paragraphs =
      child.tagName === "P"
        ? [child]
        : Array.from(child.getElementsByTagName("p"));

    paragraphs.forEach((para) => {
      const id = para.getAttribute("id");

      if (id?.startsWith(DOX_ID_PREFIX)) {
        rowByParagraph[Number(id.slice(DOX_ID_PREFIX.length))] = rowIndex;
      }
    });
  });

  return rowByParagraph;
};

const parseArrayBuffer = async (arrayBuffer) => {
  // Because docx-preview has to render its HTML _somewhere_,
  // use this orphan Element to house the HTML, from which we'll
  // extract the juicy, juicy HTML page data ...
  const dummyEl = document.createElement("div");
  const dummyStylesEl = document.getElementById(DOCX_PREVIEW_STYLES_ID);
  dummyStylesEl.textContent = "";

  // If page-breaks exist, ignore them. We wanna virtualize the document
  // consistently, so we need the DOCX to be translated to one long HTML node.
  await renderAsync(arrayBuffer, dummyEl, dummyStylesEl, {
    className: DOCX_PREVIEW_ID,
    inWrapper: false,
    renderHeaders: false,
    renderFooters: false,
    renderFootnotes: false,
    renderEndnotes: false,
    renderAltChunks: false,
  });

  const pages = dummyEl.getElementsByTagName("section");

  let paragraphOffset = 0;
  const documentParagraphs = [];
  let i = 0,
    baseWidth = 0,
    page;

  for (i; i < pages.length; i += 1) {
    page = pages[i];
    const paragraphs = page.getElementsByTagName("p");
    const pageNumber = i + 1;
    const width = toNumber(page.style.width);
    const paddingLeft = toNumber(page.style.paddingLeft);
    const paddingRight = toNumber(page.style.paddingRight);
    baseWidth = Math.max(width + paddingLeft + paddingRight, baseWidth);
    let para,
      j = 0;

    page.setAttribute("id", `${PAGE_ID_PREFIX}${pageNumber}`);
    page.dataset.paragraphOffset = paragraphOffset;

    for (j; j < paragraphs.length; j += 1) {
      para = paragraphs[j];
      const { textContent } = para;
      const hasNested = Boolean(para.querySelector("p"));
      const isEmpty = !textContent.trim();

      if (hasNested || isEmpty) {
        continue;
      }

      const paragraphIndex = j + paragraphOffset;
      const normalized = normalizeAnchorText(textContent);

      if (normalized) {
        documentParagraphs.push({
          ordinal: paragraphIndex,
          pageNumber,
          normalized,
        });
      }

      para.setAttribute("id", `${DOX_ID_PREFIX}${paragraphIndex}`);
    }

    paragraphOffset += paragraphs.length;
  }

  return {
    baseWidth,
    documentParagraphs,
    nodes: Array.from(pages),
    paragraphRows: buildParagraphRowMap(pages),
  };
};

const parseWithFileReader = (file) => {
  return new Promise((resolve) => {
    const fileReader = new FileReader();

    fileReader.onload = async function (evt) {
      resolve(await parseArrayBuffer(evt.target?.result));
    };

    fileReader.readAsArrayBuffer(file);
  });
};

const parseDocXFile = async (file) => {
  if (file instanceof File) {
    return parseWithFileReader(file);
  }

  const arrayBuffer = await downloadFileFromUrl(file);
  return parseArrayBuffer(arrayBuffer);
};

export default parseDocXFile;
